import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { connect } from 'react-redux'
import useOrderStyle from './Order.style'
import { Tooltip, withStyles, Fade, Checkbox, Box } from '@material-ui/core'
import { downloadURI } from '../../utils/utils'
import { useTakeback } from '../../hooks/useTakeback'
import ImgKitHandler from '../ImgKitHandler/ImgKitHandler'
import Price from '../Shop/Price'
import { RightOfWithdrawalInformations } from '../../typings/orders'
import Button from '../Button/Button'
import CMSText from '../Global/CMSText'
import DownloadLink from '../Global/DownloadLink'
import Loader from '../Loader/Loader'
import { useTranslation } from 'react-i18next'
import { LOCAL_STORAGE_KEYS } from '../../utils/constants'
import { useRouter } from 'next/router'
import ReturningReasonForm from './ReturningReasonForm'
import { DynamicFormDocumentFlexible } from '@/interfaces/common/prismicDynamicForm'

const MainTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    fontSize: 12,
    textAlign: 'center',
    padding: theme.spacing(1),
    width: '100%',
  },
  arrow: {
    color: theme.palette.secondary.main,
  },
}))(Tooltip)

const mapStateToProps = (state) => ({
  user: state.user,
  stores: state.stores,
})

type Tprops = {
  takeback: any
  main: any
  account: any
  order: any
  user: any
  stores: any
  currentOrderNumber: string
  postProductToReturn: (data: object) => any
  getRightOfWithdrawalInfo: (id: string) => any
  savForm?: DynamicFormDocumentFlexible
  onOpenSavForm?: (orderNumber: string) => void
  settings?: any
}

const SecondHandOrderList = ({
  takeback,
  order,
  main,
  user,
  currentOrderNumber,
  postProductToReturn,
  account,
  stores,
  getRightOfWithdrawalInfo,
  savForm,
  onOpenSavForm,
  settings,
}: Tprops) => {
  const { t } = useTranslation()
  const classes = useOrderStyle()
  const { productCheckState } = useTakeback()
  const states = productCheckState(takeback)
  const [
    secondHandRightOfWithdrawalInformations,
    setSecondHandRightOfWithdrawalInformations,
  ] = useState<RightOfWithdrawalInformations[]>([])
  const [isManageOrderButtonDisabled, setIsManageOrderButtonDisabled] =
    useState<boolean>(true)
  const [activeManageOrder, setActiveManageOrder] = useState(false)
  const [productSelection, setProductSelection] = useState(false)
  const [productToReturn, setProductToReturn] = useState([])
  const [productReturningReasons, setProductReturningReasons] = useState({})
  const warehouseId =
    stores?.selectedStore?.warehouseId || process.env.CUSTOM_WAREHOUSE || null
  const puidList = []
  const tooltipWithdrawalText = takeback?.order_withdrawal_text[0]?.text
  const [downloadLink, setDownloadLink] = useState<string>('')
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false)
  const { query } = useRouter()

  const fetchRightOfWithdrawal = async (currentOrderNumber) => {
    const rightOfWithdrawalResponse = await getRightOfWithdrawalInfo(
      currentOrderNumber
    )
    const secondHandRightOfWithdrawalInformations =
      getSecondHandRightOfWithdrawalInformations(
        rightOfWithdrawalResponse.response
      )
    const isDisabled = isAtLeastOneSecondHandItemIsReturnable(
      secondHandRightOfWithdrawalInformations
    )
    setIsManageOrderButtonDisabled(isDisabled)
    setSecondHandRightOfWithdrawalInformations(
      secondHandRightOfWithdrawalInformations
    )
  }

  const getSecondHandRightOfWithdrawalInformations = (
    rightOfWithdrawalInformations
  ) => {
    return rightOfWithdrawalInformations?.filter((rightOfWithdrawalInfo) =>
      puidList.includes(rightOfWithdrawalInfo.puid)
    )
  }

  // in order to know if order manage button si disabled or not.
  const isAtLeastOneSecondHandItemIsReturnable = (
    secondHandRightOfWithdrawalInformations: RightOfWithdrawalInformations[]
  ) => {
    return !!secondHandRightOfWithdrawalInformations?.find(
      (info) => info.returnable === true
    )
  }

  const handleActiveCurrentOrder = () => {
    if (savForm) {
      onOpenSavForm && onOpenSavForm(currentOrderNumber)
      return
    }
    setActiveManageOrder(true)
  }

  const cancelReturn = () => {
    // we reset everything
    setActiveManageOrder(false)
    setProductSelection(false)
    setProductToReturn([])
    setDownloadLink('')
    setProductReturningReasons({})
  }

  const handleProductSelection = (
    Sku,
    id,
    quantity,
    token,
    item,
    orderNumber?: string,
    warehouseId?: string,
    carrierId?: number
  ) => {
    setProductSelection(true)
    const productSelectionArray = [...productToReturn]

    // we first check if the product selected is already in the array of selected products
    if (productToReturn.find((product) => product.id === id)) {
      // if it is true the we remove the row in the array and we adapt the prices
      const index = productToReturn.findIndex((product) => product.id === id)
      productSelectionArray.splice(index, 1)
      // if nothing selected, disable generate button
      setProductSelection(productSelectionArray.length !== 0)

      // Remove returning reasons for this product
      const newReturningReasons = { ...productReturningReasons }
      delete newReturningReasons[id]
      setProductReturningReasons(newReturningReasons)
    } else {
      // if it is false we push a new row in the array of selected products and we adapt the prices
      productSelectionArray.push({
        id: id,
        Sku: Sku,
        expand: true,
        product: item.variant,
        quantity: quantity,
        orderToken: token,
        orderNumber: orderNumber,
        warehouseId: warehouseId,
        carrierId: carrierId,
      })
    }
    setProductToReturn(productSelectionArray)
  }

  const handleReturningReasonChange = (
    productId: number,
    returningRaisonId: number | null
  ) => {
    setProductReturningReasons((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        returningRaisonId,
      },
    }))
  }

  const handleReturningReasonCommentChange = (
    productId: number,
    returningComment: string
  ) => {
    setProductReturningReasons((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        returningComment,
      },
    }))
  }

  const sendProductsToReturn = async () => {
    // we prepare the data to send, the send it and check if response is ok, then we download the recieved file
    setIsDownloadLoading(true)
    const productData = []

    productToReturn.map((product) => {
      const returningReason = productReturningReasons[product.id] || {}
      productData.push({
        lizeeSKU: product.Sku,
        quantity: product.quantity,
        orderToken: product.orderToken,
        returningRaisonId: returningReason.returningRaisonId,
        returningComment: returningReason.returningComment,
      })
    })

    const products = {
      products: productData,
      gender: user?.user?.gender,
      warehouseId: productToReturn[0].warehouseId,
      carrierId: productToReturn[0].carrierId,
      omsOrderId: productToReturn[0].orderNumber,
      rightOfWithdrawal: true,
      email: undefined,
    }

    if (!localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN)) {
      products.email = query.email
    }

    const data = await postProductToReturn(products)
    if (data.type === 'POST_PRODUCT_TO_RETURN_SUCCESS') {
      if (data?.response[0]?.pdfUrl) {
        await setDownloadLink(data?.response[0]?.pdfUrl)
        await downloadURI(
          data.response[0].pdfUrl,
          `${process.env.STORE}_${data.response[0].trackingCode}`
        )
        await setIsDownloadLoading(false)
      }
    } else {
      setIsDownloadLoading(false)
    }
  }
  useEffect(() => {
    fetchRightOfWithdrawal(currentOrderNumber)
  }, [])

  // Group units by quality for display when quantity selector is enabled
  const renderSecondHandItems = () => {
    return order?.secondHandOrderList.map((secondHandItem) => {
      // Add all puids to the list for right of withdrawal checks
      secondHandItem?.units?.forEach((unit) => puidList.push(unit.puid))

      // Group by quality if quantity selector is enabled
      if (settings?.second_hand_has_quantity_selector) {
        const groupedByQuality = secondHandItem?.units?.reduce((acc, unit) => {
          const quality = unit?.productCondition
          if (!acc[quality]) {
            acc[quality] = {
              units: [],
              totalPrice: 0,
            }
          }
          acc[quality].units.push(unit)
          acc[quality].totalPrice +=
            unit?.productPrice ||
            secondHandItem.total / secondHandItem.units.length
          return acc
        }, {} as Record<string, { units: any[]; totalPrice: number }>)

        return Object.entries(groupedByQuality || {}).map(
          (
            [quality, group]: [string, { units: any[]; totalPrice: number }],
            groupIndex
          ) => {
            // Check if any unit in the group is returnable
            const isAnyReturnable = group.units.some(
              (unit) =>
                secondHandRightOfWithdrawalInformations?.find(
                  (info) => info.puid === unit.puid
                )?.returnable
            )
            const isSelected = productToReturn.find(
              (product) => product.id === `${secondHandItem.id}-${quality}`
            )
            const productReturningReason =
              productReturningReasons[`${secondHandItem.id}-${quality}`] || {}
            const hasReturnedUnits = group.units.some((unit) => unit.isReturned)

            return (
              <React.Fragment
                key={`${secondHandItem.id}-${quality}-${groupIndex}`}
              >
                <div className={classes.productCard}>
                  {activeManageOrder && (
                    <div
                      className={clsx(classes.productCardCheckbox, 'checkbox')}
                    >
                      <Fade in={true}>
                        <Checkbox
                          checked={isSelected}
                          name={secondHandItem.product.name}
                          data-testid={`order__checkbox--${quality}-${groupIndex}`}
                          disableRipple
                          size="small"
                          disabled={!isAnyReturnable}
                          onChange={() =>
                            handleProductSelection(
                              secondHandItem.product.variants[0].sku,
                              `${secondHandItem.id}-${quality}`,
                              group.units.length,
                              order?.tokenValue,
                              secondHandItem,
                              order.orderNumber,
                              warehouseId,
                              order.shipments[0].method.carrierId
                            )
                          }
                        />
                      </Fade>
                    </div>
                  )}
                  <div className={classes.productCardImg}>
                    <ImgKitHandler
                      classes={classes.imgProduct}
                      imagePaths={secondHandItem.product.images[0]}
                      settings={{
                        width: 50,
                        ar: '1-1',
                        defaultImage: main?.data?.default_image,
                      }}
                    />
                  </div>
                  <div className={classes.productCardContent}>
                    {hasReturnedUnits && (
                      <div
                        className={clsx(
                          classes.productName,
                          classes.productReturned
                        )}
                      >
                        <CMSText
                          asText
                          data={takeback?.product_returned}
                          defaultText={t('texts:account:product_returned')}
                        />
                      </div>
                    )}
                    <div className={classes.productName}>
                      x{group.units.length}&nbsp;{secondHandItem.product.name}
                      <>&nbsp;-&nbsp;</>
                      {secondHandItem.product.variants[0].size}
                    </div>
                    <div className={classes.productName}>
                      {states[`${quality}`]?.title}
                    </div>
                    <div className={classes.productName}>
                      <Price amount={group.totalPrice} />
                    </div>
                  </div>
                </div>

                {isSelected && activeManageOrder && (
                  <ReturningReasonForm
                    productId={`${secondHandItem.id}-${quality}`}
                    returningRaisonId={
                      productReturningReason.returningRaisonId || null
                    }
                    returningComment={
                      productReturningReason.returningComment || ''
                    }
                    onReturningRaisonChange={handleReturningReasonChange}
                    onReturningCommentChange={
                      handleReturningReasonCommentChange
                    }
                    account={account}
                    t={t}
                  />
                )}
              </React.Fragment>
            )
          }
        )
      }

      // Default behavior: display each unit individually
      return secondHandItem?.units?.map((secondHandUnit, index) => {
        const isDisabledCheckbox =
          !secondHandRightOfWithdrawalInformations?.find(
            (unit) => unit.puid === secondHandUnit.puid
          )?.returnable
        const isSelected = productToReturn.find(
          (product) => product.id === secondHandItem.id
        )
        const productReturningReason =
          productReturningReasons[secondHandItem.id] || {}

        return (
          <React.Fragment key={`${secondHandUnit.puid}-${index}`}>
            <div className={classes.productCard}>
              {activeManageOrder && (
                <div className={clsx(classes.productCardCheckbox, 'checkbox')}>
                  <Fade in={true}>
                    <Checkbox
                      checked={isSelected}
                      name={secondHandItem.product.name}
                      data-testid={`order__checkbox--${index}`}
                      disableRipple
                      size="small"
                      disabled={isDisabledCheckbox}
                      onChange={() =>
                        handleProductSelection(
                          secondHandItem.product.variants[0].sku,
                          secondHandItem.id,
                          1,
                          order?.tokenValue,
                          secondHandItem,
                          order.orderNumber,
                          warehouseId,
                          order.shipments[0].method.carrierId
                        )
                      }
                    />
                  </Fade>
                </div>
              )}
              <div className={classes.productCardImg}>
                <ImgKitHandler
                  classes={classes.imgProduct}
                  imagePaths={secondHandItem.product.images[0]}
                  settings={{
                    width: 50,
                    ar: '1-1',
                    defaultImage: main?.data?.default_image,
                  }}
                />
              </div>
              <div className={classes.productCardContent}>
                {secondHandUnit.isReturned && (
                  <div
                    className={clsx(
                      classes.productName,
                      classes.productReturned
                    )}
                  >
                    <CMSText
                      asText
                      data={takeback?.product_returned}
                      defaultText={t('texts:account:product_returned')}
                    />
                  </div>
                )}
                <div className={classes.productName}>
                  {secondHandItem.product.name}
                  <>&nbsp;-&nbsp;</>
                  {secondHandItem.product.variants[0].size}
                </div>
                <div className={classes.productName}>
                  {states[`${secondHandUnit.productCondition}`]?.title}
                </div>
                <div className={classes.productName}>
                  <Price amount={secondHandItem.total} />
                </div>
              </div>
            </div>

            {isSelected && activeManageOrder && (
              <ReturningReasonForm
                productId={secondHandItem.id}
                returningRaisonId={
                  productReturningReason.returningRaisonId || null
                }
                returningComment={productReturningReason.returningComment || ''}
                onReturningRaisonChange={handleReturningReasonChange}
                onReturningCommentChange={handleReturningReasonCommentChange}
                account={account}
                t={t}
              />
            )}
          </React.Fragment>
        )
      })
    })
  }

  return (
    <div>
      {renderSecondHandItems()}

      {activeManageOrder && (
        <Box
          textAlign={'center'}
          className={clsx(classes.returnCtaContainer, 'return_cta_button')}
        >
          {productSelection && downloadLink === '' ? (
            <Button
              size="small"
              data-testid={'downloadBtn'}
              variant="contained"
              color="primary"
              disabled={isDownloadLoading || !productSelection}
              onClick={() => sendProductsToReturn()}
            >
              <span className={isDownloadLoading ? classes.hide : classes.show}>
                <CMSText
                  asText
                  data={account?.data?.order_details_generate}
                  defaultText={t('texts:account:order_details_generate')}
                />
              </span>
              {isDownloadLoading && <Loader />}
            </Button>
          ) : (
            downloadLink !== '' && (
              <DownloadLink
                downloadLink={downloadLink}
                data={account?.data?.order_details_download}
                defaultText={t('texts:account:order_details_download')}
              />
            )
          )}
        </Box>
      )}

      {!activeManageOrder ? (
        <div className={classes.orderDetailsRow}>
          <MainTooltip
            placement={'top'}
            title={!isManageOrderButtonDisabled ? tooltipWithdrawalText : ''}
            arrow
          >
            <span>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={() => handleActiveCurrentOrder()}
                className={clsx(classes.orderDetailsBtn, 'btn--order-details')}
                disabled={!isManageOrderButtonDisabled}
              >
                <CMSText
                  asText
                  data={takeback?.order_second_hand_details}
                  defaultText={t('texts:account:order_second_hand_details')}
                />
              </Button>
            </span>
          </MainTooltip>
        </div>
      ) : (
        <div className={classes.orderDetailsRow}>
          <Button
            size={'small'}
            className={clsx(classes.simpleButtonDanger, classes.simpleButton)}
            onClick={() => cancelReturn()}
            disableRipple
          >
            <CMSText
              asText
              data={account?.data?.order_details_manage_cancel}
              defaultText={t('texts:account:order_details_manage_cancel')}
            />
          </Button>
        </div>
      )}
    </div>
  )
}

export default connect(mapStateToProps, null)(SecondHandOrderList)
